import { useState, useRef } from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';
import type { VoiceWidgetData } from '@/types';

interface VoiceWidgetProps {
  data: VoiceWidgetData;
}

const VoiceWidget = ({ data }: VoiceWidgetProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    const current = audioRef.current.currentTime;
    const total = audioRef.current.duration;
    setProgress((current / total) * 100);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    audioRef.current.currentTime = percentage * audioRef.current.duration;
  };

  return (
    <div className="bg-gradient-to-r from-lavender-100 to-lime-100 dark:from-dark-800 dark:to-dark-800 rounded-2xl p-4">
      <audio
        ref={audioRef}
        src={data.audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />

      <div className="flex items-center gap-4">
        <button
          onClick={togglePlay}
          className="w-12 h-12 flex items-center justify-center bg-dark-800 dark:bg-white text-white dark:text-dark-800 rounded-full hover:bg-dark-700 dark:hover:bg-gray-200 transition-colors flex-shrink-0"
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5 ml-0.5" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          {data.title && (
            <p className="font-medium text-dark-800 dark:text-white truncate mb-2">{data.title}</p>
          )}

          <div className="flex items-center gap-3">
            <div
              className="flex-1 h-2 bg-white/50 rounded-full cursor-pointer"
              onClick={handleProgressClick}
            >
              <div
                className="h-full bg-dark-800 dark:bg-white rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>

            <span className="text-xs text-dark-600 dark:text-gray-400 flex-shrink-0">
              {formatTime((progress / 100) * duration)} / {formatTime(duration)}
            </span>
          </div>
        </div>

        <Volume2 className="w-5 h-5 text-dark-600 dark:text-gray-400 flex-shrink-0" />
      </div>
    </div>
  );
};

export default VoiceWidget;
